if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onChange.bind(this));
      }

      connectedCallback() {
        // Initialize selected card UI based on any pre-checked input
        this.syncSelectedCardUI();
        // Ensure the initially-selected card is visible (e.g., when URL has ?variant=)
        // Use rAF so layout is settled before measuring/scrolling
        requestAnimationFrame(() => this.scrollSelectedIntoView());

        // Preselect second variant for the Packs product wherever it appears
        try {
          const productUrl = this.productUrl;
          if (productUrl && productUrl.includes('/products/packs')) {
            const radios = this.querySelectorAll('input[type="radio"][name="id"]');
            if (radios.length > 1 && !radios[1].checked) {
              radios[1].checked = true;
              radios[1].dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } catch (e) {
          // noop
        }

        /*
         * IMPORTANT — DO NOT REMOVE ("escrito en roca")
         * Some DOM structures inside the label can prevent native label→radio behavior
         * on certain devices. This guarded click fallback ensures radios are checked
         * and a change event is dispatched so downstream logic runs reliably (Bundles!).
         */
        // Ensure clicks on the card label always toggle selection and fire change
        this.addEventListener('click', (e) => {
          const label = e.target && e.target.closest ? e.target.closest('label.variant-option') : null;
          if (!label || !this.contains(label)) return;
          const input = label.querySelector('input[type="radio"][name="id"]');
          if (!input) return;
          if (input.disabled || label.getAttribute('aria-disabled') === 'true') {
            e.preventDefault();
            return;
          }
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get productUrl() {
        return this.productInfo?.dataset?.url || window.location.pathname;
      }

  onChange(event) {
        const target = event.target;
        if (!target) return;

        // Card flow: radios named `id` select a variant directly
        if (target.name === 'id') {
          if (target.disabled) return;

          // Keep the quantity summary in sync on every card-picker instance.
          // Shopify replaces <variant-selects> after a choice, so this must live
          // on the custom element instead of relying only on a one-time listener.
          this.syncQuantitySummary(target);

          // Update UI selection styles and ARIA
          this.syncSelectedCardUI(target);
          // On mobile, gently center the selected card horizontally
          this.scrollSelectedIntoView(target);

          /*
           * IMPORTANT — DO NOT REMOVE ("escrito en roca")
           * Immediately update the hidden [name=id] inputs inside the product forms
           * so checkout/add-to-cart pick the correct variant even before async fetches complete.
           * Do NOT dispatch a change event here to avoid loops — product-info will handle it.
           */
          // Immediately update hidden inputs in product forms so cart/checkout use the correct variant
          try {
            const productInfo = this.productInfo;
            if (productInfo) {
              const sectionId = productInfo.dataset.section;
              productInfo
                .querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}`)
                .forEach((formEl) => {
                  const idInput = formEl.querySelector('input[name="id"]');
                  if (idInput) {
                    idInput.value = target.value;
                    idInput.removeAttribute('disabled');
                    // Do not dispatch a change event here to avoid loops; product-info will trigger a change after updating
                  }
                });
            }
          } catch (e) {
            console.warn('Failed to set hidden variant id', e);
          }

          publish(PUB_SUB_EVENTS.variantIdSelectionChange, {
            data: {
              event,
              target,
              variantId: target.value,
              productUrl: this.productUrl,
            },
          });
          return;
        }

        // Options flow: collect selected values in order
        const optionValues = [];

        // Select dropdowns
        this.querySelectorAll('select[name^="options["]').forEach((sel) => {
          if (sel.value) optionValues.push(sel.value);
        });

        // Radio groups (button or swatch)
        const radioGroups = new Map();
        this.querySelectorAll('input[type="radio"][name*="-" ]:checked').forEach((radio) => {
          radioGroups.set(radio.name, radio.value);
        });
        for (const [, value] of radioGroups) optionValues.push(value);

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: optionValues,
          },
        });
  }

      syncSelectedCardUI(currentInput) {
        const labels = this.querySelectorAll('label.variant-option');
        labels.forEach((label) => {
          const input = label.querySelector('input[type="radio"][name="id"]');
          if (!input) return;
          const isSelected = currentInput ? input === currentInput : input.checked;
          label.classList.toggle('selected', isSelected);
          label.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          label.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      syncQuantitySummary(input) {
        const title = input.closest('label.variant-option')?.querySelector('.variant-title')?.textContent || '';
        const shotsPerPack = Number(input.dataset.shotsPerPack || title.match(/\b(6|12|18|30)\b/)?.[1] || 0);
        const summary = this.productInfo?.querySelector('[data-vj-quantity-summary]');
        const total = summary?.querySelector('[data-vj-total-shots]');
        if (!summary || !total || shotsPerPack <= 0) return;

        const quantity = Math.max(Number(this.productInfo.querySelector('.quantity__input')?.value || 1), 1);
        summary.dataset.shotsPerPack = String(shotsPerPack);
        total.textContent = `${quantity * shotsPerPack} shots en total`;
      }

      scrollSelectedIntoView(currentInput) {
        try {
          // Only apply on mobile breakpoints
          if (!window.matchMedia('(max-width: 749px)').matches) return;
          const container = this.querySelector('.variants-container');
          if (!container) return;
          const selectedLabel = currentInput
            ? currentInput.closest('label.variant-option')
            : this.querySelector('label.variant-option.selected');
          if (!selectedLabel) return;

          // Check if fully visible horizontally
          const cRect = container.getBoundingClientRect();
          const rRect = selectedLabel.getBoundingClientRect();
          const fullyVisible = rRect.left >= cRect.left && rRect.right <= cRect.right;
          if (fullyVisible) return;

          // Compute delta to center the card horizontally within the container
          const delta = rRect.left - (cRect.left + (cRect.width - rRect.width) / 2);
          // Use rAF to ensure layout after class toggles
          requestAnimationFrame(() => {
            container.scrollBy({ left: delta, behavior: 'smooth' });
          });
        } catch (e) {
          // noop
        }
      }
    }
  );
}
